/* Shared printing-identity + finish helpers for the sell side.
 *
 * These started life inside snippets/printing-picker.liquid. The inline sell grid
 * (assets/sell-grid.js) needs byte-identical printing identity and finish
 * fallbacks — if the modal and the grid disagree about what counts as "the same
 * printing", the same physical card lands as two different sell-draft lines and
 * the quote splits. So they live here once and both consume them.
 *
 * Loaded via a plain <script src>, so a page rendering both the picker and the
 * grid emits the tag twice; the second is a cache hit and the guard below makes
 * the re-run a no-op.
 */
window.PrintingUtils = window.PrintingUtils || (function () {
  // Local, so this file has no load-order dependency on assets/ttt-utils.js.
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Card name reduced to a comparable form: drop the second face of a DFC and any
  // punctuation/casing, so "Fire // Ice" and "fire  ice" match.
  function baseName(s) {
    return String(s || '').toLowerCase().split('//')[0].replace(/[^a-z0-9]+/g, ' ').trim();
  }

  var FIN_LABEL = {
    nonfoil: 'Non-Foil', normal: 'Normal', foil: 'Foil', etched: 'Etched',
    holofoil: 'Holofoil', reverse_holofoil: 'Reverse Holofoil',
    reverseholofoil: 'Reverse Holofoil', coldfoil: 'Cold Foil'
  };
  function finLabel(f) {
    f = String(f || '').toLowerCase();
    return FIN_LABEL[f] || (f ? f.replace(/[_-]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }) : 'Non-Foil');
  }
  /* ── Treatments ──────────────────────────────────────────────────────────
   *
   * MIRROR of loyalty-app/src/lib/treatments.ts — keep the two in sync; that file
   * is the source of truth and its header explains why one exists at all.
   *
   * The theme previously kept three hand-maintained copies of this vocabulary
   * (card-search-engine.js TREATMENTS / ADV_TREATMENTS / BARE_TREATMENTS), and they
   * had drifted: "Step-and-Repeat Foil" and "Textured Foil" match no label the
   * backend ever emits, so those two filter values could never return a result.
   */
  var TREATMENT_ORDER = [
    'Borderless', 'Showcase', 'Extended Art', 'Retro Frame', 'Full Art', 'Oversized',
    'Serialized', 'Box Topper', 'Textured',
    'Surge Foil', 'Galaxy Foil', 'Double Rainbow', 'Rainbow Foil', 'Halo Foil', 'Gilded',
    'Oil Slick', 'Confetti Foil', 'Ripple Foil', 'Neon Ink', 'Raised Foil', 'Compleat Foil',
    'First Place Foil',
    'Foil Etched', 'Stamped', 'Promo', 'Reserved List'
  ];
  // Legacy / near-miss spellings → canonical. Docs written before the vocabulary was
  // unified keep their old strings until price-sync and a reindex re-run, so rows have
  // to agree at RENDER time rather than waiting on the data.
  // NOTE: no 'inverted' alias. It used to map to 'Retro Frame' because the derivation read
  // frame_effects "inverted" as retro. That was wrong — "inverted" is the dark/translucent
  // overlay frame on full-bleed art (Double Feature, borderless profiles, Secret Lair), and
  // a borderless card is never retro-framed. Verified: zero prod products and zero index docs
  // carry the string, so nothing depends on the old mapping.
  var TREATMENT_ALIASES = {
    'retro': 'Retro Frame',
    'textured foil': 'Textured',
    'step-and-repeat foil': 'Compleat Foil', 'step and repeat foil': 'Compleat Foil',
    'etched': 'Foil Etched', 'etched foil': 'Foil Etched'
  };
  var TREATMENT_SEP = ' · ';
  var ORDER_INDEX = {};
  TREATMENT_ORDER.forEach(function (l, i) { ORDER_INDEX[l.toLowerCase()] = i; });
  // Longest first so "Retro Frame" beats "Retro" and "Double Rainbow" beats "Rainbow Foil"
  // when chewing through a space-joined string.
  var SPLIT_CANDIDATES = TREATMENT_ORDER.concat(Object.keys(TREATMENT_ALIASES))
    .sort(function (a, b) { return b.length - a.length; });

  function normalizeTreatment(raw) {
    var t = String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim();
    if (!t) return '';
    return TREATMENT_ALIASES[t.toLowerCase()] || t;
  }
  // Split the legacy space-joined form ("Borderless Surge Foil") back into labels. Half
  // these labels contain a space, so it's a greedy longest-match walk, not a split(' ').
  // An unmatched tail is kept verbatim rather than silently swallowed.
  function splitJoinedTreatment(raw) {
    var rest = String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim(), out = [];
    while (rest) {
      var hit = null;
      for (var i = 0; i < SPLIT_CANDIDATES.length; i++) {
        var cand = SPLIT_CANDIDATES[i];
        if (rest.toLowerCase().indexOf(cand.toLowerCase()) !== 0) continue;
        // Must end on a word boundary, or an unknown label that merely STARTS with a known
        // one gets torn in half — "Promotional Foil" → ["Promo", "tional Foil"].
        if (rest.length !== cand.length && rest.charAt(cand.length) !== ' ') continue;
        hit = cand; break;
      }
      if (!hit) { out.push(rest); break; }
      out.push(normalizeTreatment(hit));
      rest = rest.slice(hit.length).trim();
    }
    return out;
  }
  // Any stored shape (index array, legacy joined string, null) → canonical ordered list.
  function normalizeTreatments(raw) {
    if (raw == null) return [];
    var parts = [];
    (Array.isArray(raw) ? raw : [raw]).forEach(function (r) {
      parts = parts.concat(splitJoinedTreatment(String(r == null ? '' : r)));
    });
    var seen = {}, out = [];
    parts.forEach(function (p) {
      var label = normalizeTreatment(p); if (!label) return;
      var k = label.toLowerCase(); if (seen[k]) return;
      seen[k] = 1; out.push(label);
    });
    // Unknown labels sink to the end in arrival order — a brand-new Scryfall promo type
    // still shows up rather than being dropped or shuffling the known ones around.
    return out.sort(function (a, b) {
      var ai = ORDER_INDEX[a.toLowerCase()], bi = ORDER_INDEX[b.toLowerCase()];
      return (ai == null ? 1e9 : ai) - (bi == null ? 1e9 : bi);
    });
  }
  /* Treatments that describe a FINISH rather than the whole printing.
   *
   * Scryfall/MTGJSON hang `surgefoil` on the printing, but a Commander-deck printing is ONE
   * card object holding both finishes under one collector number (MSC #210 Skullclamp: plain
   * nonfoil deck copy + surge foil). Asserting the label at printing grain badges the plain
   * copy as a Surge Foil, which is not a card that exists — it tells a seller to hunt for a
   * premium card that isn't in their pile, and a grader to expect one that never arrives. */
  var FINISH_SCOPED = {};
  ['Surge Foil', 'Galaxy Foil', 'Double Rainbow', 'Rainbow Foil', 'Halo Foil', 'Gilded',
    'Oil Slick', 'Confetti Foil', 'Ripple Foil', 'Neon Ink', 'Raised Foil', 'Compleat Foil',
    'First Place Foil', 'Textured', 'Foil Etched'].forEach(function (l) { FINISH_SCOPED[l.toLowerCase()] = 1; });
  var NONFOIL_FINISHES = { nonfoil: 1, normal: 1, 'non-foil': 1, regular: 1 };

  /* The printing's treatments as they apply to ONE copy at a known finish. Pass a finish
   * wherever a row names a specific physical card (a sell-draft line, a submitted item);
   * omit it on surfaces that stand for the whole printing (search tiles, the picker), where
   * "this printing comes in surge foil" is exactly what the reader wants to know. */
  function treatmentsForFinish(raw, finish) {
    var all = normalizeTreatments(raw);
    if (finish == null) return all;
    if (!NONFOIL_FINISHES[String(finish).trim().toLowerCase()]) return all;
    return all.filter(function (t) { return !FINISH_SCOPED[t.toLowerCase()]; });
  }

  /* The named foil layer of a printing, or '' — the FOIL_TREATMENTS labels only. Excludes
   * Foil Etched, which is its own finish with its own button and must never hijack the
   * plain foil one. At most one applies: a printing has a single foil layer. */
  var FOIL_LAYERS = ['Surge Foil', 'Galaxy Foil', 'Double Rainbow', 'Rainbow Foil', 'Halo Foil',
    'Gilded', 'Oil Slick', 'Confetti Foil', 'Ripple Foil', 'Neon Ink', 'Raised Foil',
    'Compleat Foil', 'Textured'];
  var FOIL_LAYER = {};
  FOIL_LAYERS.forEach(function (l) { FOIL_LAYER[l.toLowerCase()] = l; });
  function foilLayerOf(raw) {
    var hit = '';
    normalizeTreatments(raw).forEach(function (t) {
      if (!hit && FOIL_LAYER[t.toLowerCase()]) hit = FOIL_LAYER[t.toLowerCase()];
    });
    return hit;
  }

  /* The label for ONE printing's finish button.
   *
   * `finLabel` alone says "Foil" for every foil, which on a dual-finish printing leaves the
   * tile reading "SURGE FOIL" over a "Non-Foil" and a "Foil" button — the seller can't tell
   * which of the two the treatment is describing, and the plain deck copy is the one they're
   * usually holding. Naming the layer on the button itself settles it: "Non-Foil" / "Surge
   * Foil". Only the traditional `foil` finish is renamed; etched keeps "Etched" and every
   * non-foil finish keeps its own name. Labels are display-only — the finish VALUE handed to
   * the draft and the quote is untouched — and none is longer than the "Reverse Holofoil"
   * these buttons already carry for Pokémon, so the shared row heights don't move. */
  function finLabelFor(fin, treatments) {
    if (String(fin || '').toLowerCase() !== 'foil') return finLabel(fin);
    return foilLayerOf(treatments) || finLabel(fin);
  }

  /* Prepare a treatment list for a row with limited horizontal space.
   * `max` is a per-surface label budget; whatever is cut stays reachable via `full`
   * on hover, so nothing is lost — only deferred. `finish` scopes the foil-layer labels
   * to one copy (see treatmentsForFinish); omit it for printing-grain surfaces. */
  function treatmentDisplay(raw, max, finish) {
    var all = treatmentsForFinish(raw, finish);
    var limit = Math.max(0, max == null ? 2 : max);
    var shown = all.slice(0, limit);
    var overflow = all.length - shown.length;
    return {
      all: all, shown: shown, overflow: overflow,
      full: all.join(TREATMENT_SEP),
      text: shown.join(TREATMENT_SEP) + (overflow > 0 ? ((shown.length ? ' ' : '') + '+' + overflow) : '')
    };
  }
  // Plain-text fragment ('' when the card has no treatment).
  function treatmentText(raw, max, finish) { return treatmentDisplay(raw, max, finish).text; }
  /* Inline HTML for a row: the labels, then a "+N" chip, the whole thing carrying the
   * full list as a title so hovering reveals what didn't fit. Styles are inline because
   * the theme's Tailwind build is purged — a new utility class here would silently
   * do nothing. Returns '' for a plain printing so callers can concatenate blindly. */
  function treatmentHtml(raw, max, opts) {
    var d = treatmentDisplay(raw, max, (opts || {}).finish);
    if (!d.all.length) return '';
    var o = opts || {};
    var color = o.color || '#8A6D1F';
    return '<span class="ttt-treat" title="' + esc(d.full) + '" style="color:' + color + ';font-weight:600">'
      + esc(d.shown.join(TREATMENT_SEP))
      + (d.overflow > 0
        ? '<span style="margin-left:.25em;padding:0 .25em;border-radius:3px;background:rgba(138,109,31,.14);font-size:.9em">+' + d.overflow + '</span>'
        : '')
      + '</span>';
  }
  // The treatments carried by a search-index doc, canonicalized. Docs predating the
  // unified vocabulary can hold either shape, so route both through normalizeTreatments.
  function treatmentsOf(d) {
    return normalizeTreatments((d && (d.treatments || d.treatment)) || []);
  }

  // Whether a finish should read as "shiny" in the UI (gets the iridescent chip).
  // Deliberately excludes the plain finishes, including Pokémon's "normal".
  function isFoilish(f) {
    f = String(f || '').toLowerCase();
    if (!f || f === 'nonfoil' || f === 'normal') return false;
    return /foil|holo|etched/.test(f);
  }

  // The backend's game code for a doc or a normalized card. '' = not a game the buylist
  // knows. Whether we buy it ONLINE is a backend setting: TTT.buylist.buysOnline(game).
  function gameOf(d) {
    var g = String((d && (d.game || d.productType)) || '');
    if (/mtg|magic/i.test(g)) return 'MTG';
    if (/pok[eé]mon|pokemon/i.test(g)) return 'POKEMON';
    if (/riftbound/i.test(g)) return 'RIFTBOUND';
    if (/star\s*wars|swu/i.test(g)) return 'SWU';
    return '';
  }

  /* The finishes a printing actually comes in.
   *
   * Prefer catalog_finishes (the full Scryfall/TCG set for the printing —
   * nonfoil/foil/etched), because when SELLING it doesn't matter what we stock.
   * Fall back to the finishes we've carried, then to the game's plain finish.
   *
   * That last fallback is deliberately narrow: docs predating the catalog_finishes
   * reindex would otherwise be offered every finish the game has, which invites a
   * seller to add a foil that printing was never issued in. A plain-only tile is
   * recoverable (the card modal's picker still offers the full list); a fabricated
   * finish silently mis-prices the submission.
   */
  function finishesFor(d) {
    var raw = (d && d.catalog_finishes && d.catalog_finishes.length) ? d.catalog_finishes : ((d && d.finishes) || []);
    var seen = {}, out = [];
    raw.forEach(function (x) { var k = String(x || '').toLowerCase(); if (k && !seen[k]) { seen[k] = 1; out.push(k); } });
    out.sort(function (a, b) { return (a === 'nonfoil' || a === 'normal' ? 0 : 1) - (b === 'nonfoil' || b === 'normal' ? 0 : 1); });
    if (out.length) return out;
    return gameOf(d) === 'POKEMON' ? ['normal'] : ['nonfoil'];
  }

  /* Every finish present across a set of printings, in one canonical order — the row
   * plan a grid of tiles shares.
   *
   * Printings of one card don't come in the same finishes: two tiles offer Non-Foil
   * and Foil, the third also offers Etched. Rendered per tile, that slides the third
   * tile's buttons out of step, so "Foil +" on one tile sits beside "Etched +" on its
   * neighbour — the exact misread that mis-prices a submission. Give the grid one row
   * per finish instead and let a tile leave the rows it doesn't offer empty.
   */
  var FIN_ORDER = ['nonfoil', 'normal', 'foil', 'holofoil', 'reverse_holofoil', 'reverseholofoil', 'etched', 'coldfoil'];
  function finishSlots(docs) {
    var seen = {}, out = [];
    (docs || []).forEach(function (d) {
      finishesFor(d).forEach(function (f) { if (f && !seen[f]) { seen[f] = 1; out.push(f); } });
    });
    out.sort(function (a, b) {
      // Anything the map doesn't know sorts after what it does, alphabetically, so a
      // finish we've never seen still lands in the same row on every tile.
      var ia = FIN_ORDER.indexOf(a), ib = FIN_ORDER.indexOf(b);
      if (ia < 0) ia = FIN_ORDER.length;
      if (ib < 0) ib = FIN_ORDER.length;
      if (ia !== ib) return ia - ib;
      return a < b ? -1 : (a > b ? 1 : 0);
    });
    return out;
  }

  /* The same plan, one per game, for a grid that can hold more than one.
   *
   * The sell grid searches every game at once, and Magic and Pokémon share no finish
   * names — pooled, a mixed result gives every tile a row for all six, three of them
   * blank. Aligning a Magic tile's rows with a Pokémon tile's buys nothing anyway.
   */
  function finishSlotsByGame(docs) {
    var by = {}, out = {};
    (docs || []).forEach(function (d) {
      var g = gameOf(d) || 'other';
      (by[g] = by[g] || []).push(d);
    });
    Object.keys(by).forEach(function (g) { out[g] = finishSlots(by[g]); });
    return out;
  }

  /* Stable printing key (set code + collector number).
   *
   * A doc whose Scryfall join failed at index time has both blank, but its Model-A
   * SKU (MTG-<SET>-<NUM>-…) and handle still encode the printing — the same
   * fallback chain the buylist uses to pin a line, so the key and the eventual
   * quote always agree.
   */
  function pinKeyOf(d) {
    var p = pinOf(d);
    return (p.set || p.number) ? (p.set + '|' + p.number) : ('name:' + baseName(d && d.name));
  }
  // The (set code, collector number) pair itself — what a sell-draft line pins on.
  function pinOf(d) {
    d = d || {};
    var set = String(d.set_code || '').toLowerCase(), num = String(d.collector_number || '');
    if (set && num) return { set: set, number: num };
    var sku = '', cs = d.conditions || [];
    for (var k = 0; k < cs.length; k++) { if (cs[k] && cs[k].sku) { sku = cs[k].sku; break; } }
    var m = String(sku).toUpperCase().match(/^[A-Z]+-([A-Z0-9]+)-([0-9][^-]*)/);
    if (m) return { set: m[1].toLowerCase(), number: m[2] };
    var hm = String(d.handle || '').match(/-([a-z0-9]+)-([0-9][a-z0-9]*)$/i);
    if (hm) return { set: hm[1].toLowerCase(), number: hm[2] };
    return { set: set, number: num };
  }

  // When a printing appears more than once (legacy Storepass products can leave >1
  // active doc per set+number), keep the best doc: in-stock first, then whichever
  // carries a scryfall_id — the clean Model-A doc that pins reliably.
  function score(d) { return (d.in_stock ? 2 : 0) + (d.scryfall_id ? 1 : 0); }
  function dedupePrintings(list) {
    var by = {}, order = [];
    (list || []).forEach(function (d) {
      var k = pinKeyOf(d);
      if (by[k] == null) { by[k] = d; order.push(k); }
      else if (score(d) > score(by[k])) { by[k] = d; }
    });
    return order.map(function (k) { return by[k]; });
  }

  /* Build the SellDraft line for one printing + finish.
   *
   * Condition is always LP and is NOT chosen by the seller — everything comes in as
   * LP and the store audits the real grade at intake (LP pays the same as NM).
   *
   * `treatments` rides along because set + number alone don't tell a seller (or the
   * staffer grading the submission) that the copy in the pile is the borderless surge
   * foil rather than the plain printing — and that distinction is most of the price.
   * It is display-only: the quote still pins on set + number + finish.
   *
   * Scoped to the chosen FINISH: the index doc carries the printing's treatments, and the
   * foil-layer ones only describe the foil copy. Without this a seller who picks the nonfoil
   * of a dual-finish printing submits a line labelled "Surge Foil", and that label rides the
   * submission all the way through grading and intake.
   */
  function lineFor(d, finish, game, qty) {
    d = d || {};
    game = game || gameOf(d) || 'MTG';
    var pin = pinOf(d);
    var fin = finish || (game === 'POKEMON' ? 'normal' : 'nonfoil');
    var treatments = treatmentsForFinish(treatmentsOf(d), fin);
    return {
      name: d.name || '',
      setName: d.set_name || '',
      setCode: pin.set || '',
      number: pin.number || '',
      finish: fin,
      condition: 'LP',
      qty: Math.max(1, qty || 1),
      game: game,
      image: d.image_url || '',
      treatments: treatments,
      // The vanity name printed on a reskin. Rides along for the same reason `treatments`
      // does: the seller found this card by the name on its FACE, and every staff surface
      // downstream names it by the oracle name, so without this the buylist row and the
      // physical card look like two different cards to whoever grades the pile.
      printedName: (d.flavor_name && d.flavor_name !== d.name) ? d.flavor_name : ''
    };
  }

  /* The canonical name line for a reskin: "<printed name> - <oracle name>".
   *
   * ONE form, everywhere — the Shopify product title, the search tile, the typeahead row and
   * the buylist line all read the same way, so a shopper who searched "Ifrit" and a picker
   * holding the slip are looking at the same string. Mirrors `cardDisplay().headline` in
   * lib/treatments.ts; the printed name leads because that is what is on the card. */
  function printedHeadline(printedName, name) {
    var p = String(printedName == null ? '' : printedName).trim();
    var n = String(name == null ? '' : name).trim();
    // Scryfall emits a flavor_name equal to the card name, and both faces of a DFC share one;
    // either would render "Sol Ring - Sol Ring".
    return p && p !== n ? p + ' - ' + n : n;
  }

  /* Typeahead rows from index docs, collapsed to one row per CARD.
   *
   * The index is one doc per printing, so a raw list is eight rows of the same card.
   *
   * Reskinned printings also carry a printed "vanity" name — Fury (SLD #7007) is
   * printed as "Ifrit" — and the index matches on it, so someone typing the printed
   * name gets a hit whose real name looks unrelated to what they typed. Those rows
   * are labelled "Ifrit (Fury)" and lead the list: typing a printed name is an
   * unambiguous request for that specific card, so it should outrank fuzzy matches
   * on real names.
   *
   * `name` is always the REAL card name, so choosing a row searches every printing
   * (the reskin among them) rather than the one reskinned printing.
   */
  function vanityAcRows(results, term, limit) {
    var t = String(term || '').toLowerCase(), seen = {}, rows = [];
    (results || []).forEach(function (d) {
      var name = String(d.name || ''); if (!name) return;
      var fn = String(d.flavor_name || '');
      var vanity = !!fn && fn.toLowerCase().indexOf(t) !== -1 && name.toLowerCase().indexOf(t) === -1;
      var label = vanity ? printedHeadline(fn, name) : name;
      var k = label.toLowerCase();
      if (seen[k]) return;
      seen[k] = 1;
      rows.push({ label: label, name: name, vanity: vanity, image: d.image_url || '', game: d.game || '' });
    });
    // Stable partition — vanity rows first, server relevance order kept within each.
    var v = [], p = [];
    rows.forEach(function (r) { (r.vanity ? v : p).push(r); });
    return v.concat(p).slice(0, limit || 8);
  }

  return {
    esc: esc, baseName: baseName, finLabel: finLabel, isFoilish: isFoilish,
    finLabelFor: finLabelFor, foilLayerOf: foilLayerOf,
    vanityAcRows: vanityAcRows, printedHeadline: printedHeadline,
    gameOf: gameOf, finishesFor: finishesFor,
    finishSlots: finishSlots, finishSlotsByGame: finishSlotsByGame,
    pinOf: pinOf, pinKeyOf: pinKeyOf,
    dedupePrintings: dedupePrintings, lineFor: lineFor,
    TREATMENT_ORDER: TREATMENT_ORDER, TREATMENT_SEP: TREATMENT_SEP,
    normalizeTreatment: normalizeTreatment, normalizeTreatments: normalizeTreatments,
    splitJoinedTreatment: splitJoinedTreatment, treatmentsOf: treatmentsOf,
    treatmentsForFinish: treatmentsForFinish,
    treatmentDisplay: treatmentDisplay, treatmentText: treatmentText, treatmentHtml: treatmentHtml
  };
})();
